import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

export interface Room {
  id: string;
  venueId: string;
  name: string;
  capacity: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RoomList {
  items: Room[];
}

export interface CreateRoomInput {
  name: string;
  capacity: number;
}

export interface UpdateRoomInput {
  name: string;
  capacity: number;
}

const listKey = (venueId: string) => ['venues', venueId, 'rooms'] as const;

export const getRooms = (venueId: string): Promise<RoomList> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/rooms', {
      params: { path: { venueId } },
    }),
  ) as Promise<RoomList>;

export const createRoom = (venueId: string, input: CreateRoomInput): Promise<Room> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/rooms', {
      params: { path: { venueId } },
      body: input,
    }),
  ) as Promise<Room>;

export const updateRoom = (
  venueId: string,
  roomId: string,
  input: UpdateRoomInput,
): Promise<Room> =>
  unwrap(
    apiClient.PATCH('/api/v1/venue-workspace/venues/{venueId}/rooms/{roomId}', {
      params: { path: { venueId, roomId } },
      body: input,
    }),
  ) as Promise<Room>;

export const setRoomActive = (venueId: string, roomId: string, active: boolean): Promise<Room> =>
  unwrap(
    apiClient.PATCH('/api/v1/venue-workspace/venues/{venueId}/rooms/{roomId}/active', {
      params: { path: { venueId, roomId } },
      body: { active },
    }),
  ) as Promise<Room>;

export function useRooms(venueId: string | null) {
  return useQuery({
    queryKey: listKey(venueId ?? ''),
    queryFn: () => getRooms(venueId!),
    enabled: venueId !== null,
  });
}

function useInvalidateRooms(venueId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: listKey(venueId) });
}

export function useCreateRoom(venueId: string) {
  const invalidate = useInvalidateRooms(venueId);
  return useMutation({
    mutationFn: (input: CreateRoomInput) => createRoom(venueId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateRoom(venueId: string) {
  const invalidate = useInvalidateRooms(venueId);
  return useMutation({
    mutationFn: ({ roomId, input }: { roomId: string; input: UpdateRoomInput }) =>
      updateRoom(venueId, roomId, input),
    onSuccess: invalidate,
  });
}

export function useSetRoomActive(venueId: string) {
  const invalidate = useInvalidateRooms(venueId);
  return useMutation({
    mutationFn: ({ roomId, active }: { roomId: string; active: boolean }) =>
      setRoomActive(venueId, roomId, active),
    onSuccess: invalidate,
  });
}
